// client/src/components/payment/PaymentProviderSettings.jsx
//
// Admin UI for connecting / managing the tenant's payment provider(s)
// (Stripe and/or Square). Rendered inside the TenantSettings "Payments" tab.
// All flows go through /api/payments/providers/:tenantId endpoints.
//
// BYO credentials only in v1 — no OAuth/Connect onboarding yet. Credentials
// are encrypted server-side before storage; the GET endpoint never returns
// them.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { sessionRequestConfig } from '../../services/sessionLocal';

const baseUrl = process.env.REACT_APP_BASE_URL || '';

const PROVIDER_META = {
  stripe: {
    label: 'Stripe',
    helpUrl: 'https://dashboard.stripe.com/apikeys',
    fields: [
      { key: 'secretKey', label: 'Secret Key', placeholder: 'sk_live_… or sk_test_…', type: 'password', required: true },
    ],
  },
  square: {
    label: 'Square',
    helpUrl: 'https://developer.squareup.com/apps',
    fields: [
      { key: 'accessToken', label: 'Access Token', placeholder: 'EAAA… (sandbox) or sq0atp-…', type: 'password', required: true },
      { key: 'applicationId', label: 'Application ID', placeholder: 'sandbox-sq0idb-… or sq0idp-…', type: 'text', required: true },
      { key: 'locationId', label: 'Location ID', placeholder: 'L…', type: 'text', required: true },
    ],
  },
};

export default function PaymentProviderSettings({ tenantId }) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('');
  const [editing, setEditing] = useState(null); // { provider, form }
  const [testing, setTesting] = useState(null); // provider name

  const load = async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${baseUrl}/api/payments/providers/${tenantId}`, sessionRequestConfig());
      setProviders(res.data?.providers || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = (providerName) => {
    const existing = providers.find(p => p.provider === providerName);
    const meta = PROVIDER_META[providerName];
    const form = { displayName: existing?.display_name || '', environment: existing?.environment || 'production', webhookSecret: '' };
    meta.fields.forEach(f => { form[f.key] = ''; });
    setEditing({ provider: providerName, form, isUpdate: !!existing });
  };

  const cancelEdit = () => setEditing(null);

  const handleFieldChange = (key, value) => {
    setEditing(prev => prev ? { ...prev, form: { ...prev.form, [key]: value } } : prev);
  };

  const saveProvider = async () => {
    if (!editing) return;
    const meta = PROVIDER_META[editing.provider];
    const credentials = {};
    for (const field of meta.fields) {
      const v = editing.form[field.key];
      if (field.required && !v) {
        setError(`${field.label} is required`);
        return;
      }
      if (v) credentials[field.key] = v;
    }
    setStatus(`Saving ${meta.label}…`);
    setError(null);
    try {
      await axios.post(
        `${baseUrl}/api/payments/providers/${tenantId}`,
        {
          provider: editing.provider,
          environment: editing.form.environment,
          credentials,
          webhookSecret: editing.form.webhookSecret || null,
          displayName: editing.form.displayName || null,
        },
        sessionRequestConfig()
      );
      setStatus(`✅ ${meta.label} saved`);
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Save failed');
      setStatus('');
    }
    setTimeout(() => setStatus(''), 3000);
  };

  const testProvider = async (providerName) => {
    setTesting(providerName);
    setStatus('');
    setError(null);
    try {
      const res = await axios.post(
        `${baseUrl}/api/payments/providers/${tenantId}/${providerName}/test`,
        {},
        sessionRequestConfig()
      );
      setStatus(`✅ ${PROVIDER_META[providerName].label}: ${res.data?.label || 'connected'}`);
    } catch (err) {
      setError(`${PROVIDER_META[providerName].label} test failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setTesting(null);
      setTimeout(() => setStatus(''), 4000);
    }
  };

  const setDefault = async (providerName) => {
    try {
      await axios.post(
        `${baseUrl}/api/payments/providers/${tenantId}/${providerName}/default`,
        {},
        sessionRequestConfig()
      );
      setStatus(`✅ ${PROVIDER_META[providerName].label} is now the default`);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Set-default failed');
    }
    setTimeout(() => setStatus(''), 3000);
  };

  const removeProvider = async (providerName) => {
    if (!window.confirm(`Remove ${PROVIDER_META[providerName].label}? Tenant-owned payments will fall back to platform Stripe.`)) {
      return;
    }
    try {
      await axios.delete(
        `${baseUrl}/api/payments/providers/${tenantId}/${providerName}`,
        sessionRequestConfig()
      );
      setStatus(`Removed ${PROVIDER_META[providerName].label}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Remove failed');
    }
    setTimeout(() => setStatus(''), 3000);
  };

  if (!tenantId) return null;

  return (
    <div>
      <h4 style={{ fontSize: '16px', marginBottom: '8px', color: '#008CFF' }}>Payment Provider</h4>
      <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
        Connect your own Stripe or Square account so tenant-owned charges (raffle entries, access fees) settle directly to you.
        Without a provider connected, charges route through the platform's Stripe account.
      </p>

      {error && <div style={{ color: '#dc2626', marginBottom: 12 }}>{error}</div>}
      {status && <div style={{ color: '#10b981', marginBottom: 12 }}>{status}</div>}

      {loading ? (
        <div style={{ color: '#666' }}>Loading…</div>
      ) : (
        <>
          {Object.keys(PROVIDER_META).map((name) => {
            const meta = PROVIDER_META[name];
            const existing = providers.find(p => p.provider === name);
            const isEditing = editing?.provider === name;
            return (
              <div key={name} style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 16 }}>{meta.label}</span>
                    {existing ? (
                      <span style={{ marginLeft: 12, fontSize: 13, color: '#10b981' }}>
                        Connected{existing.environment === 'sandbox' ? ' (sandbox)' : ''}
                        {existing.is_default ? ' ★ Default' : ''}
                      </span>
                    ) : (
                      <span style={{ marginLeft: 12, fontSize: 13, color: '#888' }}>Not connected</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {existing && !existing.is_default && (
                      <button type="button" onClick={() => setDefault(name)} style={smallButtonStyle}>
                        Set as default
                      </button>
                    )}
                    {existing && (
                      <button
                        type="button"
                        onClick={() => testProvider(name)}
                        disabled={testing === name}
                        style={smallButtonStyle}
                      >
                        {testing === name ? 'Testing…' : 'Test'}
                      </button>
                    )}
                    <button type="button" onClick={() => startEdit(name)} style={smallButtonStyle}>
                      {existing ? 'Edit' : 'Connect'}
                    </button>
                    {existing && (
                      <button
                        type="button"
                        onClick={() => removeProvider(name)}
                        style={{ ...smallButtonStyle, color: '#dc2626' }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                {isEditing && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #eee' }}>
                    <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
                      Find these values in your <a href={meta.helpUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#ff6800' }}>{meta.label} dashboard</a>.
                      Credentials are encrypted at rest.
                    </p>
                    <div className="form-group" style={{ marginBottom: 12 }}>
                      <label>Environment</label>
                      <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
                        {['production', 'sandbox'].map(env => (
                          <label key={env} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                            <input
                              type="radio"
                              name={`env-${name}`}
                              value={env}
                              checked={editing.form.environment === env}
                              onChange={(e) => handleFieldChange('environment', e.target.value)}
                            />
                            {env === 'production' ? 'Production' : 'Sandbox / Test'}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="form-group" style={{ marginBottom: 12 }}>
                      <label>Display name (optional)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editing.form.displayName}
                        onChange={(e) => handleFieldChange('displayName', e.target.value)}
                        placeholder="e.g. Main Square Account"
                      />
                    </div>
                    {meta.fields.map(f => (
                      <div key={f.key} className="form-group" style={{ marginBottom: 12 }}>
                        <label>{f.label}{f.required && <span style={{ color: '#dc2626' }}> *</span>}</label>
                        <input
                          type={f.type}
                          className="form-control"
                          value={editing.form[f.key] || ''}
                          onChange={(e) => handleFieldChange(f.key, e.target.value)}
                          placeholder={f.placeholder}
                          autoComplete="off"
                        />
                        {existing && f.type === 'password' && (
                          <small style={{ color: '#888' }}>Leave blank to keep existing value.</small>
                        )}
                      </div>
                    ))}
                    <div className="form-group" style={{ marginBottom: 12 }}>
                      <label>Webhook signing secret (optional but recommended)</label>
                      <input
                        type="password"
                        className="form-control"
                        value={editing.form.webhookSecret}
                        onChange={(e) => handleFieldChange('webhookSecret', e.target.value)}
                        placeholder={name === 'stripe' ? 'whsec_…' : 'Square webhook signature key'}
                        autoComplete="off"
                      />
                      <small style={{ color: '#888' }}>
                        Configure your {meta.label} webhook URL to:&nbsp;
                        <code>{`${window.location.origin.replace(/\/$/, '')}/api/payments/webhook/${name}/${tenantId}`}</code>
                      </small>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                      <button type="button" onClick={saveProvider} style={primaryButtonStyle}>Save</button>
                      <button type="button" onClick={cancelEdit} style={secondaryButtonStyle}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

const cardStyle = {
  marginBottom: 16,
  padding: 16,
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  background: '#fff',
};

const smallButtonStyle = {
  padding: '6px 10px',
  fontSize: 13,
  border: '1px solid #ddd',
  borderRadius: 4,
  background: '#f9fafb',
  cursor: 'pointer',
};

const primaryButtonStyle = {
  padding: '8px 16px',
  fontSize: 14,
  fontWeight: 600,
  color: '#fff',
  background: '#ff6800',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
};

const secondaryButtonStyle = {
  padding: '8px 16px',
  fontSize: 14,
  color: '#555',
  background: 'transparent',
  border: '1px solid #ddd',
  borderRadius: 4,
  cursor: 'pointer',
};
